<template>
  <div class="grid-panel">
    <grid-action-bar v-if="renderSearch"
        v-model="search"
    >
      <slot slot="left" name="left"></slot>
      <slot slot="right" name="right"></slot>
      <slot></slot>

    </grid-action-bar>

    <t-grid
        :auto-size="autoSize"
        :column-defs="columnDefs"
        :defaultColDef="defaultColDef"
        :data="internalData"
        :loading="loading"
        :gridOptions="gridOptions"
        :search="search"
        :rowSelection="rowSelection"
        @rowClicked="$emit('rowClicked', $event)"
        @cellClicked="$emit('cellClicked', $event)"
        @download="$emit('download', $event)"
        @online="$emit('online', $event)"
        @rowSelected="$emit('rowSelected', $event)"
        @update:gridApi="$emit('update:gridApi', $event)"
        @gridReady="onGridReady"
        :autoGroupColumnDef="autoGroupColumnDef"
        :treeData="treeData"
        :getDataPath="getDataPath"
        :groupDefaultExpanded="groupDefaultExpanded"
        :columnHoverHighlight="columnHoverHighlight"
        :showSidebar="showSidebar"
        :headerTextWrap="headerTextWrap"
        :tooltipShowDelay="tooltipShowDelay"
        :tooltipHideDelay="tooltipHideDelay"
        :rowHeight="rowHeight"
        :hideHeader="hideHeader"
    />
  </div>
</template>

<script>
import GridActionBar from '@/views/components/extra-components/GridActionBar';
import TGrid from '@/views/components/ag-grid/TGrid';
import api from '@/api';
import NotificationMixin from '@/components/mixins/NotificationMixin';
import { debounce } from 'lodash';

export default {
  name: 'Grid-Panel',
  components: {
    GridActionBar,
    TGrid,
  },
  mixins: [NotificationMixin],
  props: {
    tooltipShowDelay: {
      type: Number,
      default: 0,
    },
    tooltipHideDelay: {
      type: Number,
      default: 9999999,
    },
    headerTextWrap: {
      type: Boolean,
      default: false,
    },
    showSidebar: {
      type: Boolean,
      default: true,
    },
    columnHoverHighlight: {
      type: Boolean,
      default: false,
    },
    autoSize: {
      type: Boolean,
      default: false,
    },
    url: {
      type: [String, Function],
      required: false,
    },
    columnDefs: {
      type: Array,
      required: true,
    },
    defaultColDef: {
      type: Object,
      required: false,
      default: () => {
        return {
          minWidth: 100,
          sortable: true,
          resizable: true,
          filter: 'agSetColumnFilter',
          floatingFilter: true,
        };
      },
    },
    treeData: {
      type: Boolean,
      default: false,
    },
    groupDefaultExpanded: {
      type: Number,
      default: 0,
    },
    getDataPath: {
      required: false,
    },
    autoGroupColumnDef: {
      type: Object,
      required: false,
      default: () => {
        return {
          // minWidth: 900
        };
      },
    },
    gridOptions: {
      type: Object,
      default: () => {
      },
    },
    data: {
      type: [Array, null],
      required: false,
      default: () => null,
    },
    apiParams: {
      type: Object,
      default: () => {
      },
    },
    loadOnRender: {
      type: Boolean,
      default: true,
    },
    renderSearch: {
      type: Boolean,
      default: true,
    },
    modules: {
      type: Array,
      default: () => [],
    },
    rowHeight: {
      type: Number,
      default: 30,
    },
    rowSelection: {
      type: String,
      default: 'none',
    },
    hideHeader: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      loading: false,
      internalData: [],
      search: '',
    };
  },
  watch: {
    /**
     * Override data
    */
    data: {
      handler(newData) {
        this.internalData = newData;
      },
      immediate: true,
    },
    /**
     * Refresh data when parameters change
     */
    apiParams: {
      handler: 'refresh',
      deep: true,
    }
  },
  mounted() {
    if (this.loadOnRender && this.apiParams) {
      this.list(this.apiParams);
    }
  },
  methods: {
    onGridReady(params) {
      this.gridApi = params.api;
      this.$emit('update:gridApi', this.gridApi);
      this.$emit('gridReady', params)
    },
    /**
     * Get data
     *
     * @param params
     * @returns {Promise<void>}
     */
    async list(params = {}) {
      if (!this.url) {
        return;
      }

      try {
        this.loading = true;
        let data = [];

        // Prop could be something like this.$api.products.list;
        if (typeof this.url === 'function') {
          const response = await this.url({params});
          data = response.data.data;
        } else {
          const response = await api.list(this.url, { params });
          data = response.data.data;
        }

        // If we do have data being passed in, we don't want to set it
        if (!this.data) {
          this.internalData = data;
        }

        // Emit that we have finished fetching
        this.$emit('fetched', data);
      } catch (e) {
        console.log(e);
        this.notifyError(this.$t('UnableToLoadDataset'));
      } finally {
        this.loading = false;
      }
    },
    refresh: debounce(function(newParams) {
      this.list(newParams);
    }, 250)
  },

};
</script>

<style lang="scss">
  @import '@/assets/scss/custom/components/variables';
  .grid-panel {
    .ag-theme-material {
      font-family: Arial, Helvetica, sans-serif;
      
      .ag-header-row {
        color: #262626;
      }

      .ag-ltr .ag-has-focus .ag-cell-focus {
        border: none;
      }

      .ag-checkbox-input-wrapper.ag-checked::after {
        color: #110D0D;
      }
    }
  }

  .ag-row-group {
    &.ag-row-level-0 {
      font-weight: bold;

      .rater-buttons {
        display: none;
      }
    }

    &.ag-row-level-1 {
      font-style: italic;

      .rater-buttons {
        display: none;
      }
    }

    > .ag-cell {
      .fa-icon {
        display: none;
      }
    }
  }

  .background-grouped {
    background: $secondary;
  }

  .header-center {
    justify-content: center !important;

    .ag-header-group-cell-label,
    .ag-header-cell-label {
      justify-content: center !important;
    }
  }

  .currency-unit-gap {
    display: flex !important;
    justify-content: space-between;
  }

</style>
